import {
  Chip,
  Select as NSelect,
  type SelectProps as Props,
  SelectItem,
  SelectSection,
  type SharedSelection,
} from '@heroui/react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { isEmpty } from '@/utils';
import { cn } from '../../cn';
import { toaster } from '../../index';

export interface ISelectItem {
  label: string;
  description?: string | null;
  key: string | number;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
}
export type ISelection = SharedSelection;

export interface ISelectItemSection {
  label: string;
  items: ISelectItem[];
}

type SelectProps = Omit<Props, 'children'> & {
  options: ISelectItem[];
  sections?: ISelectItemSection[];
  children?: Props['children'];
  chips?: boolean;
  renderItem?: (item: ISelectItem) => React.ReactNode;
};

export const Select = ({
  options,
  className,
  sections,
  children,
  chips,
  renderItem,
  ...props
}: SelectProps) => {
  const nested = sections
    ? sections.map((s) => (
        <SelectSection key={s.label} showDivider title={s.label}>
          {s.items.map((o) => (
            <SelectItem
              startContent={o.startContent}
              endContent={o.endContent}
              textValue={o.label}
              className='text-default-700 capitalize'
              key={o.key}
            >
              {renderItem ? (
                renderItem(o)
              ) : (
                <div className='flex flex-col'>
                  <span className='text-small'>{o.label}</span>
                  {o.description && (
                    <span className='text-tiny truncate text-default-400'>{o.description}</span>
                  )}
                </div>
              )}
            </SelectItem>
          ))}
        </SelectSection>
      ))
    : options.map((o) => (
        <SelectItem
          startContent={o.startContent}
          endContent={o.endContent}
          className='text-default-700 capitalize'
          key={o.key}
          textValue={o.label}
        >
          {renderItem ? (
            renderItem(o)
          ) : (
            <div className='flex flex-col'>
              <span className='text-small'>{o.label}</span>
              {o.description && <span className='text-tiny text-default-400'>{o.description}</span>}
            </div>
          )}
        </SelectItem>
      ));
  return (
    <NSelect
      renderValue={
        chips
          ? (items) => {
              return (
                <div className='flex flex-wrap gap-2'>
                  {items.map((item) => (
                    <Chip key={item.key}>{(item.data as any)?.label}</Chip>
                  ))}
                </div>
              );
            }
          : undefined
      }
      className={cn('placeholder:text-default-400', className)}
      popoverProps={{
        ...props.popoverProps,
        classNames: {
          content: 'rounded-md',
          ...props.popoverProps?.classNames,
        },
      }}
      classNames={{ value: 'capitalize' }}
      radius='sm'
      {...props}
    >
      {children || nested}
    </NSelect>
  );
};

export interface FormSelectProps<T extends FieldValues> extends SelectProps {
  name: Path<T>;
  control: Control<T, unknown>;
  onValueChange?: (value: PathValue<T, Path<T>>) => void;
  transform?: (val: string) => PathValue<T, Path<T>>;
  showSelected?: boolean;
  transformSelected?: (val: string) => string;
  showError?: boolean;
  maxSelected?: number;
  shouldUnregister?: boolean;
}

export const FormSelect = <T extends FieldValues>({
  control,
  onValueChange,
  transform,
  name,
  selectionMode = 'single',
  description,
  showSelected = false,
  showError = true,
  transformSelected,
  maxSelected,
  shouldUnregister = undefined,
  ...rest
}: FormSelectProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid, isDirty } }) => {
        const descriptionVal =
          selectionMode === 'single'
            ? description
            : showSelected && value
              ? `${transformSelected ? value.map(transformSelected).join(', ') : value}`
              : description;
        const onHandleChange = (keys: SharedSelection) => {
          if (selectionMode === 'multiple') {
            const val = keys ? Array.from(keys) : [];
            if (maxSelected && val.length > maxSelected) {
              toaster.warning(`You can only select up to ${maxSelected} items`);
              return;
            }
            if (transform && val.length > 0) {
              const newVal = val.map(transform as never);
              onChange(newVal);
              if (onValueChange) {
                onValueChange(newVal as never);
              }
              return;
            }
            if (onValueChange) {
              onValueChange(val as never);
            }
            onChange(val);
            return;
          }

          const val = keys ? Array.from(keys).join('') : keys;
          if (transform && !isEmpty(val)) {
            const newVal = transform(val);
            onChange(newVal);
            if (onValueChange) {
              onValueChange(newVal);
            }
            return;
          }
          if (onValueChange) {
            onValueChange(val as never);
          }
          onChange(val);
        };
        return (
          <Select
            {...rest}
            selectedKeys={
              selectionMode === 'multiple'
                ? value
                  ? new Set(value.map(String))
                  : undefined
                : value != null
                  ? [`${value}`]
                  : undefined
            }
            selectionMode={selectionMode}
            onBlur={onBlur}
            description={error?.message ? '' : descriptionVal}
            onSelectionChange={onHandleChange}
            isInvalid={isDirty && invalid}
            errorMessage={showError ? error?.message : undefined}
          />
        );
      }}
    />
  );
};
